import React, { createContext, useContext, useEffect, useState, useCallback, useRef } from "react";
import axios from "axios";

/* Dedicated B2B API client + auth context, fully separate from the retail
   customer flows and from the admin back-office. */
const BACKEND_URL = process.env.REACT_APP_BACKEND_URL || "";

export const b2bApi = axios.create({ baseURL: `${BACKEND_URL}/api` });

b2bApi.interceptors.request.use((config) => {
  const token = localStorage.getItem("levami_b2b_token");
  if (token) config.headers.Authorization = `Bearer ${token}`;
  return config;
});

const B2BContext = createContext(null);

export function B2BProvider({ children }) {
  const [account, setAccount] = useState(null);
  const [loading, setLoading] = useState(true);
  const [catalog, setCatalog] = useState(null);
  const [catalogLoading, setCatalogLoading] = useState(false);
  const [catalogError, setCatalogError] = useState(false);
  const catalogRequest = useRef(null);
  const catalogRef = useRef(null);

  useEffect(() => {
    const token = localStorage.getItem("levami_b2b_token");
    if (!token) {
      setLoading(false);
      return;
    }
    b2bApi
      .get("/b2b/me")
      .then((res) => setAccount(res.data))
      .catch(() => localStorage.removeItem("levami_b2b_token"))
      .finally(() => setLoading(false));
  }, []);

  const login = useCallback(async (email, password) => {
    const res = await b2bApi.post("/b2b/login", { email, password });
    const { token, ...profile } = res.data;
    localStorage.setItem("levami_b2b_token", token);
    catalogRequest.current = null;
    catalogRef.current = null;
    setCatalog(null);
    setCatalogLoading(false);
    setCatalogError(false);
    setAccount(profile);
    return res.data;
  }, []);

  const logout = useCallback(() => {
    localStorage.removeItem("levami_b2b_token");
    localStorage.removeItem("levami_b2b_cart");
    catalogRequest.current = null;
    catalogRef.current = null;
    setCatalog(null);
    setCatalogLoading(false);
    setCatalogError(false);
    setAccount(null);
  }, []);

  const refreshAccount = useCallback(async () => {
    const { data } = await b2bApi.get("/b2b/me");
    setAccount(data);
    return data;
  }, []);
  const updateProfile = useCallback(async (profile) => {
    const { data } = await b2bApi.put("/b2b/me", profile);
    setAccount(data);
    return data;
  }, []);
  const changePassword = useCallback(async (current, next) => {
    const { data } = await b2bApi.post("/b2b/change-password", { current_password: current, new_password: next });
    if (data.token) localStorage.setItem("levami_b2b_token", data.token);
  }, []);

  const loadCatalog = useCallback(async (force = false) => {
    if (!account) return null;
    if (!force && catalogRef.current) return catalogRef.current;
    if (!force && catalogRequest.current) return catalogRequest.current;

    setCatalogLoading(true);
    setCatalogError(false);
    const request = b2bApi.get("/b2b/catalog").then((res) => {
      if (catalogRequest.current === request) {
        catalogRef.current = res.data;
        setCatalog(res.data);
      }
      return res.data;
    }).catch((error) => {
      if (catalogRequest.current === request) setCatalogError(true);
      throw error;
    }).finally(() => {
      if (catalogRequest.current === request) {
        setCatalogLoading(false);
        catalogRequest.current = null;
      }
    });
    catalogRequest.current = request;
    return request;
  }, [account]);

  return (
    <B2BContext.Provider value={{ account, loading, login, logout, refreshAccount, updateProfile, changePassword, catalog, catalogLoading, catalogError, loadCatalog }}>
      {children}
    </B2BContext.Provider>
  );
}

export function useB2B() {
  const ctx = useContext(B2BContext);
  if (!ctx) throw new Error("useB2B must be used within B2BProvider");
  return ctx;
}
